//dependencies
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounceValue, useResizeObserver } from "usehooks-ts";

import { faArrowLeft, faPlus } from "@fortawesome/pro-solid-svg-icons";

import { Pagination } from "@performyard/py-components";

//components
import Button from "../../../../../components/Button";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Typography from "../../../../../components/Text";
import AlignmentListFilters, { AlignmentFilters } from "./components/Filters";
import GoalLineage from "./components/GoalLineage";
import GoalLineageTable from "./components/GoalLineageTable";
import ReadableGoalsFilterChips from "./components/ReadableFiltersList";
import TopLevelGoals from "./components/TopLevelGoals";

// hooks
import {
	SearchServiceSort,
	useRbacPrimaryRolePermissions,
	useSearchService,
	useSearchServicePrefetch,
} from "../../../../../api/hooks";
import { useListState } from "../../../../../hooks/useListState/useListState";

// services
import { SERVICE_GOAL, getSearch } from "../../../../../api/services/defs/search-service";
import { Goal } from "../../../../../api/services/searchServiceResultTypes/goal";

// utils
import { getGoalAlignmentSearchServiceFilters } from "./utils";

//pageTemplate
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import { GOALS_SIDE_NAVIGATION_LINKS } from "../../constants";
// css
import css from "./Alignment.module.scss";
// config
import config from "./config";

type AlignmentProps = {
	userID: string;
	navItems: Array<SideNavigationLink>;
};

const Alignment = ({ userID, navItems }: AlignmentProps) => {
	const { goal_id: goalID } = useParams<{ goal_id: string }>();
	const hasSelection = !!goalID;

	const navigate = useNavigate();

	const [filters, setFilters] = useState<Partial<AlignmentFilters>>({
		status: ["active"],
	});
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [currentSort, setCurrentSort] = useState<SearchServiceSort>({ name: "name", direction: "asc" });
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [searchValue] = useDebounceValue(searchTerm, 500);

	const { clear: clearSelectedGoals, setList: setSelectedGoals, list: selectedGoals } = useListState();

	const mainContainerRef = useRef<HTMLDivElement>(null);
	const { width: appWidth = 0 } = useResizeObserver({
		ref: mainContainerRef as React.RefObject<HTMLElement>,
	});

	const { data: permissions } = useRbacPrimaryRolePermissions("goal");
	const canUserCreateGoals = permissions?.includes("goals:createGoal");

	const goalWizardBaseUrl = "/goal/wizard/";
	const goalWizardUrlParams = `assignee=${userID}${goalID ? `&alignmentID=${goalID}` : ""}`;

	const lastSelection = selectedGoals[selectedGoals.length - 1];

	/*
        Here. we are detecting if a user is navigating directly to a goal lineage view.
        Then if they are, we are getting the goal and all of its ancestors and setting them as the top level goals.

        Also, we set information for the filters and sorting to be used in the lineage view or when leaving the lineage view.
    */
	useEffect(() => {
		const getGoals = async () => {
			const initialFilter = { _id_in: [goalID] };
			const topGoal = await getSearch<Goal>(SERVICE_GOAL, initialFilter, 0, config.pageSize, currentSort);

			const idsToSearch = topGoal?.results[0].ancestors || [];
			goalID && idsToSearch?.push(goalID);

			const filterForSled = { _id_in: idsToSearch };
			const topGoals = await getSearch<Goal>(SERVICE_GOAL, filterForSled, 0, config.pageSize, currentSort);

			const orderedResults = topGoals?.results.sort((a, b) => {
				return idsToSearch.indexOf(a._id) - idsToSearch.indexOf(b._id);
			});

			setSelectedGoals(orderedResults);
		};

		if (goalID) {
			getGoals();
		} else {
			clearSelectedGoals();
		}

		setCurrentSort({ name: "name", direction: "asc" });
		setCurrentPage(1);
	}, [goalID]);

	const pageSize = config.pageSize;

	const { data, isLoading } = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: getGoalAlignmentSearchServiceFilters(filters, searchValue, !hasSelection, lastSelection?._id),
		skip: (currentPage - 1) * pageSize,
		limit: pageSize,
		sort: currentSort,
		defaultErrorMessage: "There was an error getting Alignment Goals",
	});

	// prefetch page before and 1 pages after
	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getGoalAlignmentSearchServiceFilters(filters, searchValue, !hasSelection, lastSelection?._id),
			skip: (currentPage - 2) * pageSize,
			limit: pageSize,
			sort: currentSort,
		},
		currentPage <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Goal>(
		{
			service: SERVICE_GOAL,
			filters: getGoalAlignmentSearchServiceFilters(filters, searchValue, !hasSelection, lastSelection?._id),
			skip: (currentPage + 1) * pageSize,
			limit: pageSize,
			sort: currentSort,
		},
		1
	);

	const goals = data?.results || [];
	const totalGoalsCount = data?.total || 0;

	useEffect(() => {
		setCurrentPage(1);
	}, [filters]);

	const actionButtons = [
		canUserCreateGoals && (
			<Button
				key="create-goal"
				testid="create-goal-button"
				href={`${goalWizardBaseUrl}?${goalWizardUrlParams}`}
				icon={faPlus}
				color="green"
			>
				Create Goal
			</Button>
		),
	].filter(Boolean);

	const backToTopMenu = () => {
		clearSelectedGoals();
		navigate("/company/goals/alignment");
	};

	const activeFiltersCount = Object.keys(filters).filter((key) => {
		const value = filters[key];
		if (Array.isArray(value)) {
			return value.length > 0;
		}
		return value !== undefined && value !== null && key !== "currentSort";
	}).length;

	const standardToolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchTerm}
			onSearchInputChange={setSearchTerm}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={activeFiltersCount}
		/>
	);

	const toolbarActionRow = !hasSelection ? (
		standardToolbarActionRow
	) : (
		<Button className={css.backButton} key="back-to-top" onClick={backToTopMenu} icon={faArrowLeft} isHollow={true}>
			Back To Top Level Goals
		</Button>
	);

	const toolbarInformationRow = (
		<div>
			<ReadableGoalsFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
		</div>
	);

	const drawer = (
		<AlignmentListFilters
			isOpen={isFiltersOpen}
			filters={filters}
			onClose={() => setIsFiltersOpen(false)}
			onApplyFilters={(filters, sort) => {
				setFilters(filters);
				setCurrentSort(sort);
			}}
			disableSortBy={hasSelection}
		/>
	);

	const appendGoalOntoTopGoals = (goal) => {
		navigate(`/company/goals/alignment/${goal._id}`);
	};

	const setNewTopGoalsList = (newTopGoalsList) => {
		setSelectedGoals(newTopGoalsList);
		navigate(`/company/goals/alignment/${newTopGoalsList[newTopGoalsList.length - 1]._id}`);
	};

	// if we have a goalID, we are in the lineage view, otherwise we are in the top level view
	const renderedContent = hasSelection ? (
		<div>
			<div className="mt-8">
				<div className={css.lineageContainer}>
					<GoalLineage
						sledWidth={appWidth}
						topGoalsList={selectedGoals}
						changeActiveGoal={setNewTopGoalsList}
						isLoading={selectedGoals.length < 1}
					/>
				</div>
			</div>
			<div className="mt-4">
				<div className="mb-4">
					<Typography weight="semi-bold" size="paragraph-2">
						Aligned Goals
					</Typography>
				</div>
				{standardToolbarActionRow}
				<div className="mt-4">{toolbarInformationRow}</div>
				<div className="mt-4">
					<GoalLineageTable
						goals={goals}
						isFetchingGoals={selectedGoals.length < 1 || isLoading}
						handleColumnHeaderClick={(sortValue) => setCurrentSort(sortValue)}
						activeSort={currentSort.name}
						activeSortDirection={currentSort.direction}
						onRowClick={appendGoalOntoTopGoals}
					/>
				</div>
			</div>
		</div>
	) : (
		<div>
			{goals.length > 0 || isLoading ? (
				<TopLevelGoals
					topLevelGoals={goals}
					isLoading={isLoading}
					changeActiveGoal={(goal) => {
						appendGoalOntoTopGoals(goal);
					}}
				/>
			) : (
				<div className={css.emptyAlignmentWrapper}>
					<EmptySearchTableMessage mainText="No Goals found." />
				</div>
			)}
		</div>
	);

	const mainContent = (
		<div>
			<div ref={mainContainerRef} className={css.mainContent}>
				{renderedContent}
				{!isLoading && totalGoalsCount > 0 && (
					<div
						data-testid="paginationContainer"
						className="mx-auto mt-8 flex w-full max-w-[400px] justify-center"
					>
						<Pagination
							currentPage={currentPage}
							pageCount={Math.ceil(totalGoalsCount / pageSize)}
							onPageChange={(newPage: number) => setCurrentPage(newPage)}
						/>
					</div>
				)}
			</div>
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={!hasSelection && toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle="Company Goals"
			navigationLinks={navItems}
			activeNavigationLinkKey={GOALS_SIDE_NAVIGATION_LINKS[2].key}
		/>
	);
};

export default Alignment;
